import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavDropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfoSvg from 'assets/images/icons/navbar/info.svg?react';
import GuestExperienceSvg from 'assets/images/icons/navbar/guest-experience.svg?react';
import HostfullyDevicesSvg from 'assets/images/icons/navbar/hostfully-devices.svg?react';
import AdminAccessSvg from 'assets/images/icons/navbar/admin-access.svg?react';
import Tooltip from 'components/tooltip/Tooltip';
import useAnalytics from 'hooks/useAnalytics';
import useAppUser from 'hooks/useAppUser';
import useNotify from 'hooks/useNotify';
import useGetGuidebookAccessUrl from 'hooks/guidebook/useGetGuidebookAccessUrl';
import useLoginasModal from 'pages/loginas/useLoginasModal';
import useGetHostfullyDevicesUrl from 'hooks/hostfullyDevices/useGetHostfullyDevicesUrl';
import { redirectInNewTab } from 'utils/urlUtils';
import type { NavigationProps } from '../Header.types';
import ProfileAvatar from '../profileAvatar/ProfileAvatar';
import useNavigationDropdownTitle from '../useNavigationDropdownTitle';
import useNavigationMethods from '../useNavigationMethods';
import useHostfullyDevices from '../useHostfullyDevices';
import useGuidebooks from '../useGuidebooks';
import * as S from './Navigation.styles';
import Badge from './badge/Badge';
import AddLeadButton from './AddLeadButton';
import NavigationLogo from './NavigationLogo';
import DropdownBadge from './dropdownBadge/DropdownBadge';

const Navigation = ({
  navItems,
  dropdownItems,
  dropdownSecondaryItems,
}: NavigationProps) => {
  const { t } = useTranslation();
  const showAnalytics = useAnalytics();
  const { isSuperAdmin } = useAppUser();
  const { isDropdownActive, isNavItemActive } = useNavigationMethods();
  const navDropdownTitle = useNavigationDropdownTitle();
  const { getGuidebookAccessUrl, isFetching: isFetchingGuidebook } =
    useGetGuidebookAccessUrl();
  const { getHostfullyDevicesUrl } = useGetHostfullyDevicesUrl();
  const { notifyError } = useNotify();
  const { openLoginasModal } = useLoginasModal();
  const { isHostfullyDevicesEnabled } = useHostfullyDevices();
  const [isDisabled, setIsDisabled] = useState(false);
  const { isGuidebooksEnabled } = useGuidebooks();

  const dropdownHiddenElement = useRef(null);

  useEffect(() => {
    if (isFetchingGuidebook) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [isFetchingGuidebook]);

  const redirectToGuidebook = async () => {
    redirectInNewTab({
      getUrl: async () => {
        const guidebookUrl = await getGuidebookAccessUrl();
        return guidebookUrl?.data?.url;
      },
      onError: () => notifyError(t('common.guidebookAccessError')),
    });
  };

  const redirectToHostfullyDevices = async () => {
    redirectInNewTab({
      getUrl: async () => {
        const hostfullyDevicesUrl = await getHostfullyDevicesUrl();
        return hostfullyDevicesUrl?.data?.url;
      },
      onError: () => notifyError(t('common.hostfullyDevicesAccessError')),
    });
  };

  useLayoutEffect(() => {
    const handleIframeClick = () => {
      if (dropdownHiddenElement.current) {
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        dropdownHiddenElement.current.click();
      }
    };

    if (dropdownHiddenElement.current)
      window.addEventListener('iframeClick', handleIframeClick);

    return () => {
      window.removeEventListener('iframeClick', handleIframeClick);
    };
  }, [dropdownHiddenElement.current]);

  const filteredNavItems = navItems.filter((item) => {
    if (item.path === '/owner/analytics') {
      return showAnalytics;
    }
    return true;
  });

  return (
    <S.Container id="webapp-header">
      <S.Navbar>
        <NavigationLogo />
        <S.Nav pullRight className="flex-grow-1 justify-content-end">
          <AddLeadButton />

          <div
            ref={dropdownHiddenElement}
            className="hidden-dropdown-close-element"
          />

          {filteredNavItems.map((item) => (
            <S.NavItem
              active={isNavItemActive(item.path)}
              href={item.path}
              hasBadge={!!item.unreadType}
              data-testid={`nav-item-${item.labelKey}`}
              key={item.path}
            >
              {t(item.labelKey as any)}
              {!!item.unreadType && <Badge type={item.unreadType} />}
            </S.NavItem>
          ))}

          <NavDropdown
            active={isDropdownActive({ dropdownItems, dropdownSecondaryItems })}
            title={navDropdownTitle}
            id="basic-nav-dropdown"
          >
            {dropdownItems.map((item) => (
              <S.NavItemLink href={item.path} key={item.path}>
                {t(item.labelKey as any)}
              </S.NavItemLink>
            ))}

            {/* Fragments can't be used here since `NavDropdown` expects its
                immediate children to be able to receive certain props */}
            {dropdownSecondaryItems && <li className="divider" />}
            {dropdownSecondaryItems &&
              dropdownSecondaryItems.map((item) => (
                <S.NavItemLink href={item.path} key={item.path}>
                  {t(item.labelKey as any)}
                  {item.badge && (
                    <DropdownBadge labelKey={item.badge.labelKey} />
                  )}
                </S.NavItemLink>
              ))}
          </NavDropdown>

          <S.NavItem
            href="https://help.hostfully.com"
            target="_blank"
            data-testid="navbar-help-icon"
          >
            <Tooltip
              id="navbar-help-icon-tooltip"
              placement="bottom"
              text={t('common.helpAndDocumentation')}
            >
              <InfoSvg />
            </Tooltip>
          </S.NavItem>

          {isGuidebooksEnabled && (
            <S.NavItem
              disabled={isDisabled}
              href="#"
              target="_blank"
              onClick={redirectToGuidebook}
              data-testid="navbar-guest-experience-icon"
            >
              <Tooltip
                id="navbar-guest-experience-icon-tooltip"
                placement="bottom"
                text={t('common.guestExperience')}
              >
                <GuestExperienceSvg />
              </Tooltip>
            </S.NavItem>
          )}

          {isHostfullyDevicesEnabled && (
            <S.NavItem
              href="#"
              target="_blank"
              onClick={redirectToHostfullyDevices}
              data-testid="navbar-hostfully-devices-icon"
            >
              <Tooltip
                id="navbar-hostfully-devices-icon-tooltip"
                placement="bottom"
                text={t('common.hostfullyDevices')}
              >
                <HostfullyDevicesSvg />
              </Tooltip>
            </S.NavItem>
          )}

          {isSuperAdmin && (
            <S.NavItem
              href="#"
              target="_blank"
              data-testid="navbar-admin-access-icon"
              onClick={openLoginasModal}
            >
              <Tooltip
                id="navbar-admin-access-icon-tooltip"
                placement="bottom"
                text={t('common.adminAccess')}
              >
                <AdminAccessSvg />
              </Tooltip>
            </S.NavItem>
          )}

          <ProfileAvatar />
        </S.Nav>
      </S.Navbar>
    </S.Container>
  );
};

export default Navigation;
